import React, { createContext, useEffect, useState, useRef, useCallback } from "react";
import config from "../config";
import { USER_ACTION, USER_PERMISSIONS_ACTION } from "../store/actions";
import instance from "../utils/axios";
import { useDispatch } from "react-redux";
import Loader from "../components/loader";

const AuthContext = createContext();
let globalAuthCheckInProgress = false;

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const tokenVar = config?.tokenVar;
  const [loading, setLoading] = useState(true);
  const authChecked = useRef(false);
  const isRedirecting = useRef(false);

  const handleRedirect = useCallback(() => {
    // Prevent multiple redirects
    if (isRedirecting.current) return;
    isRedirecting.current = true;

    setLoading(false);

    const currentPath = window.location.pathname;
    if (
      currentPath === config?.login_uri ||
      currentPath.includes("otpVerification") ||
      currentPath.includes("forgot-password") ||
      config?.is_testing
    ) return;

    localStorage.removeItem(btoa("jesi_holiday_popup_status"));
    window.location.replace(config?.login_uri);
  }, []);

  const handleNext = useCallback((response) => {
    if (response?.status !== 200) {
      handleRedirect();
      return;
    }

    const permissions = {};
    response?.data?.user_permissions?.map((c_c) => {
      const key_ = c_c
        .replace("create", "")
        .replace("view", "")
        .replace("update", "")
        .replace("delete", "")
        .replace("seeAll", "");
      if (
        c_c.includes("view") ||
        c_c.includes("delete") ||
        c_c.includes("create") ||
        c_c.includes("update") ||
        c_c.includes("seeAll")
      ) {
        if (!permissions[key_]) permissions[key_] = {};
        if (c_c.includes("view"))
          permissions[key_]["view"] = c_c.includes("view");
        if (c_c.includes("delete"))
          permissions[key_]["delete"] = c_c.includes("delete");
        if (c_c.includes("create"))
          permissions[key_]["create"] = c_c.includes("create");
        if (c_c.includes("update"))
          permissions[key_]["update"] = c_c.includes("update");
        if (c_c.includes("seeAll"))
          permissions[key_]["list"] = c_c.includes("seeAll");
      }
    });
    dispatch({ type: USER_ACTION, payload: response?.data });
    dispatch({ type: USER_PERMISSIONS_ACTION, payload: permissions });

    if (
      response?.data?.email ||
      response?.data?.id ||
      response?.data?.role ||
      response?.data?.name
    ) {
      setLoading(false);
    }
  }, [dispatch, handleRedirect]);

  const handleRefreshUser = useCallback(async () => {
    try {
      const response = await instance.get(`${config?.api_uri}/auth/whoami`);
      handleNext(response?.data);
    } catch (error) {
      handleRedirect();
    }
  }, [handleNext, handleRedirect]);

  useEffect(() => {
    // Double check to prevent multiple executions
    if (authChecked.current || globalAuthCheckInProgress) {
      return;
    }
    
    authChecked.current = true;
    globalAuthCheckInProgress = true;

    const controller = new AbortController();
    let isMounted = true;

    const checkAuth = async () => {
      try {
        const accessToken = localStorage.getItem(tokenVar);
        const rememberMeToken = localStorage.getItem(
          btoa(config.remeber_me_token)
        );

        if (accessToken || rememberMeToken) {
          const response = await instance.get(
            `${config?.api_uri}/auth/whoami`,
            {
              signal: controller.signal,
            }
          );
          if (isMounted) {
            handleNext(response?.data);
          }
        } else {
          if (isMounted) {
            handleRedirect();
          }
        }
      } catch (error) {
        if (error.name !== "CanceledError" && isMounted) {
          console.error("Auth error:", error);
          handleRedirect();
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
      controller.abort();
      globalAuthCheckInProgress = false;
    };
  }, [tokenVar, handleNext, handleRedirect]);

  if (loading) return <Loader />;

  return (
    <AuthContext.Provider value={{ handleRefreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;